import React, { useEffect, useState } from 'react';
import {
   useAddBankAccountMutation,
   useDeleteBankAccountMutation,
   useGetBankAccountsQuery,
   useGetDefaultBankAccountQuery,
   useSetDefaultBankAccountMutation,
   useUpdateBankAccountMutation,
} from '../../../store/bank-accounts/bank-accounts.api';
import { Button } from 'react-bootstrap';
import { Plus } from 'react-bootstrap-icons';
import ModalContainer from '../../layout/modal-container';
import AddEditAccount from './add-edit-account';
import { BankAccountDto } from '../../../models/bank-account.dto';
import { useForm } from 'react-hook-form';
import './manage-bank-account.scss';
import { useTranslation } from 'react-i18next';
import { IS_ADMIN } from '../../../utils/role';
import { ReactComponent as InfoIcon } from '../../../assets/icons/info.svg';
import editIcon from '../../../assets/icons/bank-account-edit.svg';
import trashIcon from '../../../assets/icons/bank-account-trash.svg';

const ManageBankAccounts = () => {
   const [translate] = useTranslation(['account', 'common']);
   const { data: bankAccounts } = useGetBankAccountsQuery();
   const { data: defaultBankAccount } = useGetDefaultBankAccountQuery();
   const [addNewBankAccount] = useAddBankAccountMutation();
   const [updateBankAccount] = useUpdateBankAccountMutation();
   const [deleteBankAccount] = useDeleteBankAccountMutation();
   const [setDefaultBankAccount] = useSetDefaultBankAccountMutation();
   const [accountErrors, setAccountErrors] = useState([]);
   const [showEditAccountModal, setShowEditAccountModal] = useState(false);
   const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
   const [editAccount, setEditAccount] = useState<BankAccountDto | null | undefined>(undefined);
   const [deleteAccount, setDeleteAccount] = useState<BankAccountDto | null>(null);
   const [deleteError, setDeleteError] = useState<string | null>(null);
   const {
      register,
      reset,
      formState: { errors, isValid },
      handleSubmit,
      setValue,
      control,
   } = useForm<BankAccountDto>();

   const isAdmin = IS_ADMIN();

   const onSubmit = handleSubmit(async (data) => {
      try {
         let res;
         if (editAccount) {
            res = await updateBankAccount({
               bankAccountId: editAccount.bankAccountId as number,
               data,
            });
         } else {
            res = await addNewBankAccount(data);
         }

         if ('error' in res) {
            const message = res.error?.data?.message;
            const fieldErrors = res.error?.data?.fieldErrors;
            if (fieldErrors && fieldErrors.length > 0) {
               // @ts-ignore
               setAccountErrors(fieldErrors);
            } else if (message) {
               // @ts-ignore
               setAccountErrors([{ field: 'general', defaultMessage: message }]);
            } else {
               setDeleteError('An unknown error occurred.');
            }
         } else {
            closeEditModal();
         }
      } catch (err) {
         setDeleteError('Something went wrong while submitting the form.');
      }
   });

   const closeEditModal = () => {
      setShowEditAccountModal(false);
      setEditAccount(undefined);
   };

   useEffect(() => {
      if (editAccount !== undefined) {
         setAccountErrors([]);
         setShowEditAccountModal(true);
      }
   }, [editAccount]);

   useEffect(() => {
      if (deleteAccount !== null) {
         setShowDeleteAccountModal(true);
      }
   }, [deleteAccount]);

   return (
      <>
         <main className='app-page bank-accounts-page'>
            <div className='header' >
               <div className='title'>{translate('account:bankAccount.bankAccounts')}</div>
               {isAdmin &&
                  <div className='bank-account-actions'>
                     <Button onClick={() => setEditAccount(null)} className='primary' >
                        {translate('account:bankAccount.addBankAccount')} <Plus size={24} />
                     </Button>
                  </div>
               }
            </div>
            {bankAccounts &&
               bankAccounts.map((account) => (
                  <div
                     className='account'
                     key={account.bankAccountId}
                  >
                     <div className='account-name'>
                        <b>{account.name}</b>
                        {account.bankAccountId === defaultBankAccount?.bankAccountId && (
                           <span className='default-bank-account'>{translate('common:common.default')}</span>
                        )}
                     </div>

                     <div className='account-actions'>
                        <div className='default-action'>
                           {account.bankAccountId !== defaultBankAccount?.bankAccountId && (
                              isAdmin
                                 ? <Button
                                    className='default-btn'
                                    onClick={() => setDefaultBankAccount(account.bankAccountId!)}
                                 >
                                    {translate('common:common.setAsDefault')}
                                 </Button>
                                 : <></>
                           )}
                        </div>

                        {isAdmin && (
                           <div className='account-record-actions'>
                              <button
                                 type='button'
                                 className='bank-account-action'
                                 onClick={() => setEditAccount(account)}
                                 aria-label={String(translate('common:common.edit'))}
                              >
                                 <img src={editIcon} alt='' aria-hidden='true' />
                                 <span>{translate('common:common.edit')}</span>
                              </button>
                              <button
                                 type='button'
                                 className='bank-account-action'
                                 onClick={() => setDeleteAccount(account)}
                                 aria-label={String(translate('common:common.delete'))}
                              >
                                 <img src={trashIcon} alt='' aria-hidden='true' />
                                 <span>{translate('common:common.delete')}</span>
                              </button>
                           </div>
                        )}
                     </div>
                  </div>
               ))}
         </main>
         {showEditAccountModal && (
            <ModalContainer
               modalContent={
                  <AddEditAccount
                     accountErrors={accountErrors}
                     setValue={setValue}
                     errors={errors}
                     onSubmit={onSubmit}
                     register={register}
                     editAccount={editAccount}
                     reset={reset}
                     control={control}
                  />
               }
               modalHeader={
                  editAccount
                     ? translate('account:bankAccount.editAccount')
                     : 'Add a bank account'
               }
               submitButtonText={translate('common:common.save')}
               show={true}
               onSubmit={onSubmit}
               submitButtonEnabled={isValid}
               onCancel={() => closeEditModal()}
               dialogClassName='bank-account-dialog'
            ></ModalContainer>
         )}

         {showDeleteAccountModal && (
            <ModalContainer
               modalContent={
                  <>
                     {deleteError && (
                        <div className="alert alert-danger" role="alert">
                           {deleteError}
                        </div>
                     )}
                     <div className='delete-account-copy'>
                        <p><strong>{deleteAccount?.name}</strong> will be permanently deleted from your partners list.</p>
                        <p>This action is permanent and cannot be undone.</p>
                     </div>
                  </>
               }
               modalHeader={
                  <>
                     <InfoIcon /> Are you sure you want to delete this account?
                  </>
               }
               submitButtonText={translate('common:common.delete')}
               show={true}
               onSubmit={async () => {
                  const res = await deleteBankAccount(deleteAccount?.bankAccountId);

                  if ('error' in res) {
                     // @ts-ignore
                     setDeleteError(res.error.data.message || translate('common:common.errorOccurred'));
                  } else {
                     setDeleteError(null);
                     setShowDeleteAccountModal(false);
                     setDeleteAccount(null);
                  }
               }}
               onCancel={() => {
                  setDeleteError(null);
                  setDeleteAccount(null);
                  setShowDeleteAccountModal(false);
               }}
               dialogClassName='delete-account-dialog'
            />
         )}
      </>
   );
};

export default ManageBankAccounts;
